import React, { useState, useEffect } from 'react';
import { getAllowedEmails, addAllowedEmail, deleteAllowedEmail, listUsers, updateUserTier, getUserDetail, impersonateUser } from '../services/api';

const styles = {
  container: {
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: 'var(--bg-page)',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
    color: 'var(--text-primary)',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  searchInput: {
    padding: '10px 16px',
    width: '280px',
    borderRadius: '8px',
    border: '1px solid var(--border-input)',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)',
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statsBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    flex: 1,
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    border: '1px solid var(--border-default)',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '13px',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableContainer: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    border: '1px solid var(--border-default)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border-default)',
    backgroundColor: 'var(--bg-table-header)',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-default)',
    verticalAlign: 'middle',
  },
  emailCell: {
    fontWeight: '500',
    color: 'var(--text-primary)',
  },
  deleteButton: {
    padding: '6px 14px',
    backgroundColor: 'var(--badge-red-bg)',
    color: 'var(--badge-red-text)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: 'var(--text-tertiary)',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--border-light)',
    borderTop: '3px solid var(--color-primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-muted)',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--overlay-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    padding: '24px',
    width: '400px',
    maxWidth: '90%',
    boxShadow: 'var(--shadow-modal)',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '20px',
    color: 'var(--text-primary)',
  },
  modalField: {
    marginBottom: '16px',
  },
  modalLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--text-tertiary)',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border-input)',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  saveButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  cancelButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--bg-button-secondary)',
    color: 'var(--text-tertiary)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  errorBanner: {
    padding: '12px 16px',
    backgroundColor: 'var(--badge-red-bg)',
    color: 'var(--badge-red-text)',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  successBanner: {
    padding: '12px 16px',
    backgroundColor: 'var(--badge-green-bg)',
    color: 'var(--badge-green-text)',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
};

const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AccessPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);

  useEffect(() => {
    fetchEmails();
    listUsers()
      .then((data) => setUsers(data?.users || []))
      .catch((err) => console.error('Error fetching users:', err));
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllowedEmails();
      setEmails(data || []);
    } catch (err) {
      console.error('Error fetching allowed emails:', err);
      setError('Failed to load authorized emails.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;

    try {
      await addAllowedEmail(email);
      setNewEmail('');
      setIsAdding(false);
      setSuccess(`${email} added to authorized list.`);
      fetchEmails();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add email.';
      setError(msg);
    }
  };

  const handleDelete = async (id, email) => {
    try {
      await deleteAllowedEmail(id);
      setConfirmDelete(null);
      setSuccess(`${email} removed from authorized list.`);
      setEmails(emails.filter(e => e.id !== id));
    } catch (err) {
      setError('Failed to remove email.');
    }
  };

  const filteredEmails = emails.filter(
    (e) => e.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{spinnerKeyframes}</style>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Loading authorized emails...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{spinnerKeyframes}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Authorized Emails</h1>
        <div style={styles.headerActions}>
          <input
            type="text"
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <button style={styles.addButton} onClick={() => setIsAdding(true)}>
            + Add Email
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{emails.length}</div>
          <div style={styles.statLabel}>Authorized Emails</div>
        </div>
      </div>

      {/* Feedback */}
      {error && <div style={styles.errorBanner}>{error}</div>}
      {success && <div style={styles.successBanner}>{success}</div>}

      {/* Table */}
      {filteredEmails.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No authorized emails found.</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Added By</th>
                <th style={styles.th}>Date Added</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmails.map((entry) => (
                <tr key={entry.id}>
                  <td style={{ ...styles.td, ...styles.emailCell }}>{entry.email}</td>
                  <td style={styles.td}>{entry.added_by || '—'}</td>
                  <td style={styles.td}>
                    {entry.created_at
                      ? new Date(entry.created_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      style={styles.deleteButton}
                      onClick={() => setConfirmDelete(entry)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User Management */}
      <h2 style={{ fontSize: '22px', fontWeight: '600', color: 'var(--text-primary)', margin: '40px 0 16px' }}>
        User Management
      </h2>
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Properties</th>
              <th style={styles.th}>Tier</th>
              <th style={styles.th}>Last Login</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: 'var(--text-muted)' }}>
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const tierColors = {
                  starter: '#3b82f6',
                  professional: '#22c55e',
                  enterprise: '#a855f7',
                };
                return (
                  <tr key={user.id}>
                    <td style={{ ...styles.td, ...styles.emailCell }}>{user.name || '—'}</td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>{user.role}</td>
                    <td style={styles.td}>{user.property_count ?? '—'}</td>
                    <td style={styles.td}>
                      <select
                        value={user.tier || 'starter'}
                        onChange={async (e) => {
                          const newTier = e.target.value;
                          try {
                            await updateUserTier(user.id, newTier);
                            setUsers((prev) =>
                              prev.map((u) => (u.id === user.id ? { ...u, tier: newTier } : u))
                            );
                          } catch (err) {
                            console.error('Error updating tier:', err);
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'white',
                          backgroundColor: tierColors[user.tier || 'starter'] || tierColors.starter,
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        <option value="starter">starter</option>
                        <option value="professional">professional</option>
                        <option value="enterprise">enterprise</option>
                      </select>
                    </td>
                    <td style={styles.td}>
                      {user.last_login
                        ? new Date(user.last_login).toLocaleString()
                        : '—'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          style={{
                            padding: '6px 14px',
                            backgroundColor: 'var(--badge-purple-bg, #f3e8ff)',
                            color: 'var(--badge-purple-text)',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                          onClick={async () => {
                            try {
                              const detail = await getUserDetail(user.id);
                              setUserDetail(detail?.user || detail);
                              setSelectedUser(user);
                            } catch (err) {
                              console.error('Error fetching user detail:', err);
                            }
                          }}
                        >
                          View
                        </button>
                        <button
                          style={{
                            padding: '6px 14px',
                            backgroundColor: 'var(--badge-yellow-bg, #fef3c7)',
                            color: 'var(--badge-yellow-text, #92400e)',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                          onClick={async () => {
                            try {
                              const result = await impersonateUser(user.id);
                              localStorage.setItem('ayni-superadmin-token', localStorage.getItem('ayni-access-token'));
                              localStorage.setItem('ayni-access-token', result.access_token);
                              alert(`Impersonating ${result.user.name}. Refresh the page to see their view. Click "Exit Impersonation" in the top bar to return.`);
                              window.location.reload();
                            } catch (err) {
                              console.error('Error impersonating user:', err);
                              alert('Failed to impersonate user.');
                            }
                          }}
                        >
                          Impersonate
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* User Detail Panel */}
      {userDetail && (
        <div style={{
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          border: '1px solid var(--border-default)',
          padding: '24px',
          marginTop: '20px',
        }}>
          {/* Panel Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 4px' }}>
                {userDetail.name || selectedUser?.name || 'User'}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{userDetail.email || selectedUser?.email}</span>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: { starter: '#3b82f6', professional: '#22c55e', enterprise: '#a855f7' }[userDetail.tier || 'starter'] || '#3b82f6',
                }}>
                  {userDetail.tier || 'starter'}
                </span>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{userDetail.role || selectedUser?.role}</span>
              </div>
            </div>
            <button
              onClick={() => { setUserDetail(null); setSelectedUser(null); }}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--bg-button-secondary)',
                color: 'var(--text-tertiary)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          {/* Properties */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Properties ({(userDetail.properties || []).length})
            </h4>
            {(userDetail.properties || []).length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No properties.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {userDetail.properties.map((prop, idx) => (
                  <div key={idx} style={{
                    padding: '10px 14px',
                    backgroundColor: 'var(--bg-subtle)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)',
                    fontSize: '13px',
                  }}>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{prop.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>
                      {prop.city || '—'}
                      {prop.has_lock && (
                        <span style={{ marginLeft: '8px', color: 'var(--badge-green-text)', fontWeight: '600' }}>Lock</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cleaners */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Aynistas ({(userDetail.cleaners || []).length})
            </h4>
            {(userDetail.cleaners || []).length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No cleaners.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {userDetail.cleaners.map((cleaner, idx) => (
                  <div key={idx} style={{
                    padding: '10px 14px',
                    backgroundColor: 'var(--bg-subtle)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)',
                    fontSize: '13px',
                  }}>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{cleaner.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>
                      {cleaner.phone || '—'} &middot; {cleaner.language || '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Verifications */}
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Recent Verifications ({(userDetail.recent_verifications || []).length})
            </h4>
            {(userDetail.recent_verifications || []).length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No recent verifications.</p>
            ) : (
              <table style={{ ...styles.table, borderRadius: '8px', overflow: 'hidden' }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Property</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Score</th>
                    <th style={styles.th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {userDetail.recent_verifications.map((v, idx) => {
                    const statusColors = {
                      APPROVED: { bg: 'var(--badge-green-bg)', text: 'var(--badge-green-text)' },
                      FLAGGED: { bg: 'var(--badge-red-bg)', text: 'var(--badge-red-text)' },
                      REVIEW_REQUIRED: { bg: 'var(--badge-yellow-bg, #fef3c7)', text: 'var(--badge-yellow-text, #92400e)' },
                      PENDING: { bg: 'var(--bg-subtle)', text: 'var(--text-muted)' },
                    };
                    const sc = statusColors[v.status] || statusColors.PENDING;
                    return (
                      <tr key={idx}>
                        <td style={styles.td}>{v.property || '—'}</td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: sc.bg,
                            color: sc.text,
                          }}>
                            {v.status || '—'}
                          </span>
                        </td>
                        <td style={styles.td}>{v.score != null ? v.score : '—'}</td>
                        <td style={styles.td}>{v.date ? new Date(v.date).toLocaleDateString() : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Add Email Modal */}
      {isAdding && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Add Authorized Email</h2>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Email Address</label>
              <input
                style={styles.input}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@example.com"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                autoFocus
              />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.saveButton} onClick={handleAdd}>
                Add Email
              </button>
              <button
                style={styles.cancelButton}
                onClick={() => { setIsAdding(false); setNewEmail(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Remove Email</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
              Are you sure you want to remove <strong>{confirmDelete.email}</strong> from the authorized list?
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
              This person will no longer be able to create an account.
            </p>
            <div style={styles.modalActions}>
              <button
                style={{ ...styles.saveButton, backgroundColor: 'var(--badge-red-text)' }}
                onClick={() => handleDelete(confirmDelete.id, confirmDelete.email)}
              >
                Remove
              </button>
              <button
                style={styles.cancelButton}
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessPage;
